// useSocketManager.ts
import { useCallback, useMemo, useRef } from "react";
import type { RefObject } from "react";
import { io, type Socket } from "socket.io-client";
import type {
  KernelHandle,
  SocketManagerReturnType,
  SocketStatus,
} from "#types/types";

type UseSocketManagerProps = {
  kernel: RefObject<KernelHandle | null>;
};

const RECONNECTION_DELAY_MS = 1_000;
const RECONNECTION_DELAY_MAX_MS = 3_000;
const IGNORED_EVENTS = new Set<string>([
  "connect",
  "disconnect",
  "connect_error",
  "error",
  "reconnect_attempt",
  "reconnect",
  "reconnect_error",
  "reconnect_failed",
]);

export const useSocketManager = ({
  kernel,
}: UseSocketManagerProps): SocketManagerReturnType => {
  const socketRef = useRef<Socket | null>(null);
  const shouldAttemptRef = useRef(false);
  const wasConnectedRef = useRef(false);

  const updateStatus = useCallback(
    (status: SocketStatus) => {
      kernel.current?.setSocketStatus(status);
    },
    [kernel],
  );

  const disconnectSocket = useCallback(() => {
    shouldAttemptRef.current = false;
    const socket = socketRef.current;
    if (socket) {
      console.log("[SocketManager] Disconnecting socket...");
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    }
    updateStatus("idle");
    kernel.current?.setSocketReconnectAttempts(0);
    wasConnectedRef.current = false;
  }, [kernel, updateStatus]);

  const forwardEventToKernel = useCallback(
    (eventName: string, payload: unknown) => {
      const kernelInstance = kernel.current;
      if (!kernelInstance) {
        return;
      }

      try {
        kernelInstance.dispatchSocketEvent(eventName, payload);
      } catch (error) {
        console.error(
          `[SocketManager] Failed to dispatch socket event "${eventName}".`,
          error,
        );
      }
    },
    [kernel],
  );

  const connectSocket = useCallback(() => {
    if (socketRef.current) {
      return;
    }
    shouldAttemptRef.current = true;
    updateStatus("connecting");

    console.log("[SocketManager] Initializing and connecting socket...");

    // Wake the API route before the websocket handshake to reduce first-load timeouts.
    //
    // NOT /api/socket. Once the Socket.IO server is attached, engine.io owns that path and answers
    // anything that is not a valid handshake with a 400, so warming up there logged a failed
    // request on every page load. /api/realtime-warmup is the same handler on a path Next still
    // owns; see the note there.
    const ensureSocketServer = async () => {
      try {
        await fetch("/api/realtime-warmup?t=" + Date.now());
      } catch (_error) {
        // Ignore errors, just trying to wake up the server.
      }
    };

    const socket = io({
      path: "/api/socket",
      transports: ["websocket"],
      withCredentials: true,
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: RECONNECTION_DELAY_MS,
      reconnectionDelayMax: RECONNECTION_DELAY_MAX_MS,
      randomizationFactor: 0.5,
    });

    socketRef.current = socket;
    void ensureSocketServer().finally(() => {
      if (shouldAttemptRef.current) {
        socket.connect();
      }
    });

    socket.on("connect", async () => {
      console.log("[SocketManager] Socket connected successfully.");
      updateStatus("connected");
      kernel.current?.setSocketReconnectAttempts(0);

      const organisationId = kernel.current?.getUserContext().organisationId;
      const payload: { organisationId?: string; lastEventTimestamp?: string } =
        {};

      if (organisationId) {
        payload.organisationId = organisationId;
      }

      const lastTimestamp = kernel.current?.getSocketLastEventTimestamp();
      if (lastTimestamp) {
        payload.lastEventTimestamp = lastTimestamp;
      }

      console.log("[SocketManager] Joining room with payload:", payload);
      socket.emit("join-room", payload);

      const isReconnect = wasConnectedRef.current;
      wasConnectedRef.current = true;

      if (isReconnect) {
        try {
          await kernel.current?.handleSocketReconnect();
        } catch (error) {
          console.error(
            "[SocketManager] Failed to run handleSocketReconnect after reconnect.",
            error,
          );
        }
      }
    });

    socket.on("disconnect", (reason) => {
      console.warn(`[SocketManager] Socket disconnected. Reason: ${reason}`);
      updateStatus("disconnected");
      if (reason === "io server disconnect") {
        // The disconnection was initiated by the server, you need to reconnect manually
        socket.connect();
      }
    });

    socket.on("connect_error", (error) => {
      const message =
        typeof error?.message === "string"
          ? error.message
          : "Unknown socket error";
      if (message.toLowerCase().includes("timeout")) {
        console.warn(
          "[SocketManager] Connection timeout while socket server wakes up.",
        );
      } else {
        // Socket.IO retries connect errors automatically. Reporting a transient proxy/server
        // restart as a fatal console error makes Next's dev overlay blame unrelated work (most
        // visibly a generation that happened to finish at the same time). The terminal
        // reconnect_failed event below is the point where this becomes a real error.
        console.warn("[SocketManager] Connection interrupted; retrying:", message);
      }
      updateStatus(wasConnectedRef.current ? "reconnecting" : "connecting");
      // If the server restarted, the IO instance might be gone. Ping the API to re-attach it.
      void ensureSocketServer();
    });

    socket.on("reconnect_attempt", (attempt) => {
      console.log(`[SocketManager] Reconnect attempt #${attempt}...`);
      updateStatus("reconnecting");
      kernel.current?.setSocketReconnectAttempts(attempt);
      // Ensure the server is ready for the reconnection
      void ensureSocketServer();
    });

    socket.on("reconnect_failed", () => {
      console.error("[SocketManager] All reconnection attempts failed.");
      updateStatus("error");
    });

    socket.onAny((eventName: string, ...args: unknown[]) => {
      if (IGNORED_EVENTS.has(eventName)) {
        return;
      }
      const payload = args.length > 1 ? args : args[0];
      forwardEventToKernel(eventName, payload);
    });
  }, [forwardEventToKernel, kernel, updateStatus]);

  return useMemo(
    () => ({
      connectSocket,
      disconnectSocket,
      socketRef,
    }),
    [connectSocket, disconnectSocket],
  );
};
